class ProductGallerySlider extends Theme.Component {
  #index = 0;
  #phone = window.matchMedia("(max-width: 767px)");
  #reduced_motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  #swipe_distance = 40;
  #swipe = null;
  #swiped = false;
  #pending = null;
  #release_timer = null;

  connectedCallback() {
    super.connectedCallback();

    this.slides = Array.from(this.querySelectorAll(".product-gallery-slide"));
    this.dots = Array.from(this.closest(".product-gallery")?.querySelectorAll(".product-gallery-stage .scroll-dot") || []);
    this.fade_duration = parseFloat(getComputedStyle(this).getPropertyValue("--transition-duration")) || 250;

    const active = this.slides.findIndex((slide) => slide.classList.contains("state--active"));

    this.setIndex(Math.max(active, 0), { silent: true });
    this.scrollToSlide(this.#index, false);

    this.dots.forEach((dot, index) => {
      this.addDirectEvent("click", dot, () => this.select(index));
    });

    this.addDirectEvent("productgallerythumbnails:click", document, (event) => this.select(Number(event.detail.index)));
    this.addDirectEvent("productform:variantchange", document, (event) => this.selectVariant(event.detail.variant_id));
    this.addDirectEvent("click", this, (event) => this.onClick(event));
    this.addDirectEvent("keydown", this, (event) => this.onKeydown(event));
    this.addDirectEvent("scroll", this, Theme.Helpers.debounce(() => this.onScrollEnd(), 100));
    this.addDirectEvent("pointerdown", this, (event) => this.onPointerDown(event));
    this.addDirectEvent("pointerup", this, (event) => this.onPointerUp(event));
    this.addDirectEvent("pointercancel", this, () => this.#swipe = null);
    this.addDirectEvent("change", this.#phone, () => this.scrollToSlide(this.#index, false));
  }

  disconnectedCallback() {
    clearTimeout(this.#release_timer);

    super.disconnectedCallback();
  }

  selectVariant(variant_id) {
    if (!variant_id) {
      return;
    }

    const id = String(variant_id);
    const index = this.slides.findIndex((slide) => (slide.dataset.variantId || "").split(", ").includes(id));

    if (index >= 0) {
      this.select(index);
    }
  }

  async select(index) {
    if (this.slides.length < 2) {
      return;
    }

    const next = (index + this.slides.length) % this.slides.length;

    if (next === this.#index) {
      return;
    }

    if (this.#phone.matches) {
      this.scrollToSlide(next, true);
      this.setIndex(next);
      return;
    }

    this.#pending = next;

    await this.whenLoaded(this.slides[next]);

    if (this.#pending !== next) {
      return;
    }

    this.holdPrevious(this.slides[this.#index]);
    this.setIndex(next);
  }

  setIndex(index, { silent = false } = {}) {
    this.#index = index;

    this.slides.forEach((slide, slide_index) => {
      const active = slide_index === index;

      slide.classList.toggle("state--active", active);
      slide.inert = !active;

      const video = slide.querySelector("video");

      if (!video || silent) {
        return;
      }

      if (active) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    this.dots.forEach((dot, dot_index) => {
      const active = dot_index === index;

      dot.classList.toggle("state--active", active);

      if (active) {
        dot.setAttribute("aria-current", "true");
      } else {
        dot.removeAttribute("aria-current");
      }
    });
  }

  holdPrevious(slide) {
    clearTimeout(this.#release_timer);

    this.querySelectorAll(".product-gallery-slide.state--previous").forEach((element) => {
      element.classList.remove("state--previous");
    });

    if (!slide) {
      return;
    }

    slide.classList.add("state--previous");

    this.#release_timer = setTimeout(() => {
      slide.classList.remove("state--previous");
    }, this.fade_duration);
  }

  whenLoaded(slide) {
    const image = slide?.querySelector("img");

    if (!image || image.complete) {
      return Promise.resolve();
    }

    image.loading = "eager";

    return Promise.race([
      image.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 600))
    ]);
  }

  scrollToSlide(index, animate) {
    if (!this.#phone.matches) {
      return;
    }

    const smooth = animate && !this.#reduced_motion.matches;

    this.classList.toggle("state--jumping", smooth);

    this.scrollTo({
      left: index * this.clientWidth,
      behavior: smooth ? "smooth" : "auto"
    });
  }

  onScrollEnd() {
    if (!this.#phone.matches) {
      return;
    }

    this.classList.remove("state--jumping");

    const index = Math.round(this.scrollLeft / this.clientWidth);

    if (index !== this.#index) {
      this.setIndex(index);
    }
  }

  onClick(event) {
    if (this.#swiped) {
      this.#swiped = false;
      return;
    }

    if (!event.target.closest(".product-gallery-slide")) {
      return;
    }

    document.dispatchEvent(new CustomEvent("productgallery:open", {
      detail: {
        index: this.#index
      }
    }));
  }

  onKeydown(event) {
    if (event.target !== this) {
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      this.select(this.#index + (event.key === "ArrowRight" ? 1 : -1));
    }
  }

  onPointerDown(event) {
    this.#swiped = false;

    if (this.#phone.matches) {
      return;
    }

    this.#swipe = {
      x: event.clientX,
      y: event.clientY
    };
  }

  onPointerUp(event) {
    if (!this.#swipe) {
      return;
    }

    const distance_x = event.clientX - this.#swipe.x;
    const distance_y = event.clientY - this.#swipe.y;

    this.#swipe = null;

    if (Math.abs(distance_x) < this.#swipe_distance || Math.abs(distance_x) < Math.abs(distance_y)) {
      return;
    }

    this.#swiped = true;
    this.select(this.#index + (distance_x < 0 ? 1 : -1));
  }
}

customElements.define("product-gallery-slider", ProductGallerySlider);

class ProductGalleryThumbnails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const element = event.target.closest("a");

      document.dispatchEvent(new CustomEvent("productgallerythumbnails:click", {
        detail: {
          index: element.dataset.index
        }
      }));
    });
  }
}

customElements.define("product-gallery-thumbnails", ProductGalleryThumbnails);
class ProductGalleryLightbox extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();

    this.dialog = this.querySelector("dialog");
    this.slides = [...this.elements.strip.children];

    this.closest(".product-gallery").querySelectorAll("[data-gallery-index]").forEach((button) => {
      button.addEventListener("click", () => this.open(Number(button.dataset.galleryIndex)));
    });

    document.addEventListener("productgallery:open", (event) => this.open(Number(event.detail.index)));

    this.elements.close.addEventListener("click", () => this.dialog.close());
    this.elements.prev.addEventListener("click", () => this.go(this.index - 1));
    this.elements.next.addEventListener("click", () => this.go(this.index + 1));

    this.elements.strip.addEventListener("scroll", () => this.update(), { passive: true });
    this.elements.strip.addEventListener("click", (event) => this.handleClick(event));
    this.elements.strip.addEventListener("pointermove", (event) => this.handleMove(event));

    this.dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        this.go(this.index - 1);
      } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        this.go(this.index + 1);
      } else if (event.key === "Escape") {
        this.dialog.close();
      }
    });

    this.dialog.addEventListener("close", () => this.reset());
  }

  get index() {
    const top = this.elements.strip.scrollTop;

    return this.slides.reduce((nearest, slide, slide_index) => {
      const distance = Math.abs(slide.offsetTop - top);
      return distance < nearest.distance ? { index: slide_index, distance } : nearest;
    }, { index: 0, distance: Infinity }).index;
  }

  open(index) {
    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.go(index, "instant");
    this.update();
  }

  go(index, behavior = "smooth") {
    const target = Math.max(0, Math.min(index, this.slides.length - 1));

    this.elements.strip.scrollTo({
      top: this.slides[target].offsetTop,
      behavior
    });
  }

  update() {
    const index = this.index;

    this.elements.counter.textContent = `${index + 1} / ${this.slides.length}`;
    this.elements.prev.disabled = index === 0;
    this.elements.next.disabled = index === this.slides.length - 1;

    this.slides.forEach((slide, slide_index) => {
      if (slide_index !== index) {
        slide.classList.remove("state--zoomed");
      }

      const video = slide.querySelector("video");

      if (video && slide_index !== index) {
        video.pause();
      }
    });
  }

  handleClick(event) {
    const image = event.target.closest("img");

    if (!image) {
      if (!event.target.closest("video")) {
        this.dialog.close();
      }

      return;
    }

    const slide = image.closest(".product-gallery-lightbox-slide");

    this.setZoomOrigin(slide, event);
    slide.classList.toggle("state--zoomed");
  }

  handleMove(event) {
    const slide = event.target.closest(".product-gallery-lightbox-slide.state--zoomed");

    if (slide) {
      this.setZoomOrigin(slide, event);
    }
  }

  setZoomOrigin(slide, event) {
    const box = slide.querySelector(".product-gallery-lightbox-media").getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - box.left) / box.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - box.top) / box.height) * 100));

    slide.style.setProperty("--zoom-x", `${x}%`);
    slide.style.setProperty("--zoom-y", `${y}%`);
  }

  reset() {
    this.slides.forEach((slide) => {
      slide.classList.remove("state--zoomed");
      slide.querySelector("video")?.pause();
    });
  }
}

customElements.define("product-gallery-lightbox", ProductGalleryLightbox);
